import { getAnalyticsDistinctId } from 'utils/analyticsDistinctId';
import { posthog } from 'utils/posthogClient';
import { get, post } from '.';

const ORIGIN = `${window.location.origin}/`;

/** Prefer an explicit override (e.g. recovered from magic-link redirect); else current anon id. */
export const getAuthPostHogDistinctId = (override?: string): string | undefined => {
    const id = override?.trim() || getAnalyticsDistinctId(posthog);
    return id || undefined;
};

const withPostHogDistinctId = (override?: string): { posthogDistinctId: string } | Record<string, never> => {
    const posthogDistinctId = getAuthPostHogDistinctId(override);
    return posthogDistinctId ? { posthogDistinctId } : {};
};

/** Pending magic-link / verify path — identity only, no session. */
export type AuthenticatePendingResponse = {
    success: true;
    message?: string;
    userId?: number;
    email?: string;
};

/** Verified login/signup — full user + session token. */
export type AuthenticateVerifiedResponse = {
    success?: true;
    user: BizlyAPI.User;
};

export const sendMagicLink = ({
    email,
    firstName,
    lastName,
    teamName,
    phone,
    role,
    imageUrl,
    redirectUrl,
    verifyOnly,
    companySize,
    teamColor,
    referrer,
    origin = ORIGIN,
    posthogDistinctId,
}: {
    email?: string;
    firstName?: string;
    lastName?: string;
    teamName?: string;
    role?: string;
    phone?: string;
    imageUrl?: string;
    redirectUrl?: string;
    verifyOnly?: boolean;
    companySize?: string;
    teamColor?: string;
    referrer?: string;
    // Overrides the host the magic link points at (defaults to the current origin).
    origin?: string;
    /** Override for tests / recovered ids; defaults to current PostHog anon distinct id. */
    posthogDistinctId?: string;
}): Promise<AuthenticatePendingResponse> =>
    post(
        'authenticate',
        {
            email,
            teamName,
            redirectUrl,
            origin,
            useMagicLink: true,
            ...(firstName ? { firstName } : {}),
            ...(lastName ? { lastName } : {}),
            ...(phone ? { phone } : {}),
            ...(role ? { role } : {}),
            ...(imageUrl ? { imageUrl } : {}),
            ...(verifyOnly ? { verifyOnly } : {}),
            ...(companySize ? { companySize } : {}),
            ...(teamColor ? { teamColor } : {}),
            ...(referrer ? { referrer } : {}),
            ...withPostHogDistinctId(posthogDistinctId),
        },
        { requireToken: false, showErrorCode: true }
    );

export function isAuthenticationPartialSuccess(error: unknown): error is BizlyAPIError.Authentication {
    const expectedError = error as BizlyAPIError.Authentication;
    const teams = expectedError?.raw?.teams;
    return expectedError?.raw?.success === false && Array.isArray(teams) && teams.length === 0;
}

export function isSSOOnlyAccount(error: unknown): error is BizlyAPIError.Authentication {
    const expectedError = error as BizlyAPIError.Authentication;
    return expectedError?.raw?.success === false && typeof expectedError?.raw?.ssoLink === 'string';
}

export const loginWithGoogleId = ({
    email,
    googleIdToken,
    teamName,
    firstName,
    lastName,
    phone,
    role,
    imageUrl,
    verifyOnly,
    referrer,
    posthogDistinctId,
}: {
    email: string;
    googleIdToken: string;
    firstName?: string;
    lastName?: string;
    teamName?: string;
    role?: string;
    phone?: string;
    imageUrl?: string;
    verifyOnly?: boolean;
    referrer?: string;
    /** Override for tests / recovered ids; defaults to current PostHog anon distinct id. */
    posthogDistinctId?: string;
}): Promise<AuthenticateVerifiedResponse> =>
    post(
        'authenticate',
        {
            email,
            googleIdToken,
            teamName,
            ...(firstName ? { firstName } : {}),
            ...(lastName ? { lastName } : {}),
            ...(phone ? { phone } : {}),
            ...(role ? { role } : {}),
            ...(imageUrl ? { imageUrl } : {}),
            ...(verifyOnly ? { verifyOnly } : {}),
            ...(referrer ? { referrer } : {}),
            ...withPostHogDistinctId(posthogDistinctId),
        },
        { requireToken: false, showErrorCode: true }
    ) as Promise<AuthenticateVerifiedResponse>;

export const exchangeMagicToken = (token: string, posthogDistinctId?: string) =>
    post('exchange-token', { token, ...withPostHogDistinctId(posthogDistinctId) }, { requireToken: false }).catch(
        err => {
            console.error('Error at log in:', err);
            throw err;
        }
    );

export const acceptTeamInvite = (token: string, email: string, posthogDistinctId?: string) =>
    post(
        'accept-team-invite',
        { token, email, ...withPostHogDistinctId(posthogDistinctId) },
        { requireToken: false }
    ).catch(err => {
        console.error('Error at log in:', err);
        throw err;
    });

export const getLoggedInUser = () => get('me', { requireToken: true }).then(json => json.user);

export const sendVerificationCode = ({ email }: { email: string }) => {
    return post('email/request-verification', { email }, { requireToken: false })
        .then(json => json)
        .catch(error => {
            if (error?.raw) {
                return error.raw;
            }
            throw error;
        });
};

export const confirmVerificationCode = ({ email, code }: { email: string; code: string }) => {
    return post('email/verify-code', { email, code }, { requireToken: false, showErrorCode: true })
        .then(json => json)
        .catch(error => {
            if (error?.raw) {
                return error.raw;
            }
            throw error;
        });
};

export const sendPhoneVerificationCode = ({ phone }: { phone: string }) => {
    return post('users/phone/send-verification', { phone });
};

export const checkPhoneVerificationCode = ({ code }: { code: string }) => {
    return post('/users/phone/check-verification', { code });
};
